import type {
  NfcMessage,
  NfcNativeMessage,
  NfcReadEvent,
  NfcTagDetectedEvent,
} from './nfc-plugin.definitions';

// Basic URI prefix table for Well Known 'U' records (optional convenience)
const URI_PREFIX: string[] = [
  '',
  'http://www.',
  'https://www.',
  'http://',
  'https://',
  'tel:',
  'mailto:',
  'ftp://anonymous:anonymous@',
  'ftp://ftp.',
  'ftps://',
  'sftp://',
  'smb://',
  'nfs://',
  'ftp://',
  'dav://',
  'news:',
  'telnet://',
  'imap:',
  'rtsp://',
  'urn:',
  'pop:',
  'sip:',
  'sips:',
  'tftp:',
  'btspp://',
  'btl2cap://',
  'btgoep://',
  'tcpobex://',
  'irdaobex://',
  'file://',
  'urn:epc:id:',
  'urn:epc:tag:',
  'urn:epc:pat:',
  'urn:epc:raw:',
  'urn:epc:',
  'urn:nfc:',
];

// Helper encoders for well-known record types (only applied to string payloads)
const buildTextPayload = (text: string, lang = 'en'): number[] => {
  const langBytes = Array.from(new TextEncoder().encode(lang));
  const textBytes = Array.from(new TextEncoder().encode(text));
  const status = langBytes.length & 0x3f; // UTF-8 encoding, language length (<= 63)
  return [status, ...langBytes, ...textBytes];
};
const buildUriPayload = (uri: string, prefixCode = 0x00): number[] => {
  const uriBytes = Array.from(new TextEncoder().encode(uri));
  return [prefixCode, ...uriBytes];
};

// Parse NFC Forum "Text" (Well Known 'T') records according to spec.
const decodeTextPayload = (bytes: Uint8Array): string => {
  if (bytes.length === 0) return '';
  const status = bytes[0];
  const isUTF16 = (status & 0x80) !== 0; // Bit 7 indicates encoding
  const langLength = status & 0x3f; // Bits 0-5 language code length
  if (1 + langLength > bytes.length) return ''; // Corrupt
  const textBytes = bytes.slice(1 + langLength);
  try {
    const decoder = new TextDecoder(isUTF16 ? 'utf-16' : 'utf-8');
    return decoder.decode(textBytes);
  } catch {
    // Fallback: naive ASCII
    return Array.from(textBytes)
      .map((b) => String.fromCharCode(b))
      .join('');
  }
};

const decodeUriPayload = (bytes: Uint8Array): string => {
  if (bytes.length === 0) return '';
  const prefixIndex = bytes[0];
  const prefix = URI_PREFIX[prefixIndex] || '';
  const remainder = bytes.slice(1);
  try {
    return prefix + new TextDecoder('utf-8').decode(remainder);
  } catch {
    return (
      prefix +
      Array.from(remainder)
        .map((b) => String.fromCharCode(b))
        .join('')
    );
  }
};

const toStringPayload = (recordType: 'T' | 'U', bytes: Uint8Array): string => {
  // Well Known Text
  if (recordType === 'T') return decodeTextPayload(bytes);
  // Well Known URI
  if (recordType === 'U') return decodeUriPayload(bytes);
  // Default: attempt UTF-8 decode
  try {
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return Array.from(bytes)
      .map((c) => String.fromCharCode(c))
      .join('');
  }
};

export const decodeTagDetectedEvent = (
  event: NfcTagDetectedEvent,
): NfcReadEvent => {
  return {
    serialNumber: event.serialNumber,
    message: {
      records: event.message.records.map((record) => {
        return {
          type: record.type,
          payload: toStringPayload(record.type, new Uint8Array(record.payload)),
        };
      }),
    },
  };
};

export const formatWrittenPayload = (message: NfcMessage): NfcNativeMessage => {
  return {
    records: message.records.map((record) => {
      let payload: number[] | null = null;
      const recordPayload = record.payload as string | number[] | Uint8Array;

      if (typeof recordPayload === 'string') {
        // Apply spec-compliant formatting only for Well Known Text (T) & URI (U) types.
        if (record.type === 'T') {
          payload = buildTextPayload(recordPayload);
        } else if (record.type === 'U') {
          payload = buildUriPayload(recordPayload);
        } else {
          // Generic string: raw UTF-8 bytes (no extra framing)
          payload = Array.from(new TextEncoder().encode(recordPayload));
        }
      } else if (Array.isArray(recordPayload)) {
        // Assume already raw bytes; do NOT modify
        payload = recordPayload as number[];
      } else if (recordPayload instanceof Uint8Array) {
        payload = Array.from(recordPayload);
      }

      if (!payload) {
        throw new Error('Unsupported payload type');
      }

      return {
        type: record.type,
        payload,
      };
    }),
  };
};
